import React, { useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "common/src/components/widget/Select";
import KPIChartSortHeader from "./KPIChartSortHeader";
import DedicatedBarChartWidget from "./DedicatedBarChartWidget";
import ToggleSection, { useToggleSection } from "../../../widget/ToggleSection";
import ToggleTagGroupPanel from "./ToggleTagGroupPanel";
import { ChartPlotColors } from "../constants/chartColors";
import {
  toggleExcludedSiteDirectionActive as toggleExcludedSiteDirection,
  includeSelectedSiteDirectionsActive as includeSelectedSiteDirections,
  excludeSelectedSiteDirectionsActive as excludeSelectedSiteDirections,
  setIncludedSiteDirectionsActive as setIncludedSiteDirections,
  setExcludedSiteDirectionsActive as setExcludedSiteDirections,
} from "../../../../state/facade/dataFacade";
import { getVehicleTotalsChartTranslationKeys } from "./OverloadsBy";
import { selectDataMode } from "../../../../state/slice/dataModeSlice";
import {formatSLONumber} from "../utils/FormatNumbers";

/* ---------------- helpers ---------------- */
const normDir = (d) => (d && d.trim() !== "" ? d.trim() : "Unknown");
const clamp = (n) => (Number.isFinite(+n) ? Math.max(0, +n) : 0);
const normKey = (s) =>
    String(s ?? "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

function dailyTotalsByKey(
    payload = {},
    mode = "site-direction",
    sortBy = "name",
    sortDirection = "asc",
    excludedSiteDirectionNames = []
) {
  const isSDExcluded = (sdn) => excludedSiteDirectionNames.includes(String(sdn).trim());

  const groupMap = {};
  Object.entries(payload || {}).forEach(([locationId, data]) => {
    const stats = Array.isArray(data?.dailyStats) ? data.dailyStats : [];
    stats.forEach((stat) => {
      const direction = normDir(stat?.direction);
      const sdn = String(stat?.siteDirectionName || `${locationId} | ${direction}`).trim();
      if (isSDExcluded(sdn)) return;

      const key = mode === "site" ? locationId : mode === "direction" ? direction : sdn;
      if (!groupMap[key]) groupMap[key] = { name: key, total: 0 };
      groupMap[key].total += clamp(stat?.numAllVehicles ?? 0);
    });
  });

  const groupArray = Object.values(groupMap);
  groupArray.sort((a, b) => {
    const va = a[sortBy] ?? a.name;
    const vb = b[sortBy] ?? b.name;
    if (typeof va === "string" || typeof vb === "string") {
      return sortDirection === "asc"
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va));
    }
    return sortDirection === "asc" ? va - vb : vb - va;
  });

  const xAxis = groupArray.map((g) => g.name);
  const series = [
    {
      name: "dashboards_view.total",
      data: groupArray.map((g) => g.total),
      type: "bar",
    },
  ];

  return { xAxis, series, colors: [ChartPlotColors.TOTAL_MEASUREMENTS], raw: groupMap };
}

/* ---------------- component ---------------- */
const VehicleTotalsChart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isHidden, togglerKey } = useToggleSection(["footer"], ["footer"]);
  const dataSourceMode = useSelector(selectDataMode);
  const { locations = {} } = useSelector((s) =>
      dataSourceMode === "processed" ? s.processedSiteDataAPI : s.siteDataAPI
  );
  const data = useSelector((s) =>
      dataSourceMode === "processed" ? s.statisticsProcessedDataAPI.sites.data : s.statisticsDataAPI.sites.data
  );
  const isLoading = useSelector((s) =>
      dataSourceMode === "processed" ? s.statisticsProcessedDataAPI.sites.loading : s.statisticsDataAPI.sites.loading
  );
  const excludedSiteDirectionNames = useSelector(
      (s) =>
          (dataSourceMode === "processed"
              ? s.processedMapFiltersSlice.excluded?.siteDirections
              : s.mapFiltersSlice.excluded?.siteDirections) || []
  );

  // local panel highlight (NOT redux)
  const [selIncludedIds, setSelIncludedIds] = useState(new Set());
  const [selExcludedIds, setSelExcludedIds] = useState(new Set());

  // chart state
  const [mode, setMode] = useState("site-direction");
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const { xAxis, series, colors } = useMemo(
      () => dailyTotalsByKey(data, mode, sortBy, sortDirection, excludedSiteDirectionNames),
      [data, mode, sortBy, sortDirection, excludedSiteDirectionNames]
  );

  // keep series identity stable across renders so Apex doesn’t reset internal states unnecessarily
  const displaySeries = useMemo(
      () => (series ?? []).map((s) => ({ ...s, name: t(s.name) })),
      [series, t]
  );

  const { titleKey, yAxisKey } = getVehicleTotalsChartTranslationKeys(mode);

  // siteDirection toggle items (for panel)
  const allSiteDirectionItems = useMemo(() => {
    const set = new Set();
    Object.values(data || {}).forEach((bundle) => {
      (bundle?.dailyStats || []).forEach((row) => {
        if (row?.siteDirectionName) set.add(String(row.siteDirectionName));
      });
    });
    return Array.from(set)
    .map((name) => ({ name }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [data]);

  const excludedSiteDirectionItems = useMemo(
      () => excludedSiteDirectionNames.map((n) => ({ name: n })),
      [excludedSiteDirectionNames]
  );

  // Map: normalized category -> Set<siteDirectionName>
  const categoryToSiteDirections = useMemo(() => {
    const map = new Map();
    Object.entries(data || {}).forEach(([locationId, bundle]) => {
      (bundle?.dailyStats || []).forEach((row) => {
        const direction = normDir(row?.direction);
        const sdn = String(row?.siteDirectionName || `${locationId} | ${direction}`).trim();
        const key = mode === "site" ? locationId : mode === "direction" ? direction : sdn;
        const k = normKey(key);
        if (!map.has(k)) map.set(k, new Set());
        map.get(k).add(sdn);
      });
    });
    return map;
  }, [data, mode]);

  const HIGHLIGHT_OFF = (colors && colors[0]) || ChartPlotColors.TOTAL_MEASUREMENTS; // "#c9c9c9";
  const DEFAULT_BAR_COLOR = (colors && colors[0]) || ChartPlotColors.TOTAL_MEASUREMENTS;

  const perPointFill = useMemo(() => {
    const excluded = new Set(excludedSiteDirectionNames);
    return (xAxis || []).map((category) => {
      const set = categoryToSiteDirections.get(normKey(category)) ?? new Set();
      if (set.size === 0) return DEFAULT_BAR_COLOR;
      const allExcluded = Array.from(set).every((n) => excluded.has(n));
      return allExcluded ? HIGHLIGHT_OFF : DEFAULT_BAR_COLOR;
    });
  }, [xAxis, categoryToSiteDirections, excludedSiteDirectionNames, colors]);

  // click handler (functional updates; normalized lookup)
  const handleBarClick = useCallback(
      ({ category, event }) => {
        const k = normKey(category);
        const targetSet = categoryToSiteDirections.get(k);
        const targets = new Set(Array.from(targetSet || []));
        const excludedSet = new Set(excludedSiteDirectionNames);

        if (targets.size === 0) {
          setSelIncludedIds((prev) => (prev.size ? new Set() : prev));
          setSelExcludedIds((prev) => (prev.size ? new Set() : prev));
          return;
        }

        const isCtrl = !!(event?.metaKey || event?.ctrlKey);
        const isAlt = !!event?.altKey;
        const isShift = !!event?.shiftKey;

        const addAll = (set, items) => {
          let changed = false;
          for (const x of items) if (!set.has(x)) { set.add(x); changed = true; }
          return changed;
        };
        const delAll = (set, items) => {
          let changed = false;
          for (const x of items) if (set.delete(x)) changed = true;
          return changed;
        };

        if (isShift) {
          // exclude → remove from included
          setSelExcludedIds((prevExc) => {
            const nextExc = new Set(prevExc);
            addAll(nextExc, targets);
            return nextExc;
          });
          setSelIncludedIds((prevInc) => {
            const nextInc = new Set(prevInc);
            delAll(nextInc, targets);
            return nextInc;
          });
          return;
        }
        if (isCtrl || (!isShift && !isAlt)) {
          // include → remove from excluded
          setSelIncludedIds((prevInc) => {
            const nextInc = new Set(prevInc);
            addAll(nextInc, targets);
            return nextInc;
          });
          setSelExcludedIds((prevExc) => {
            const nextExc = new Set(prevExc);
            delAll(nextExc, targets);
            return nextExc;
          });
          return;
        }
        if (isAlt) {
          // flip only within targets
          setSelIncludedIds((prevInc) => {
            const nextInc = new Set(prevInc);
            setSelExcludedIds((prevExc) => {
              const nextExc = new Set(prevExc);
              const toInclude = [];
              const toExclude = [];
              for (const n of targets) {
                if (nextInc.has(n)) toExclude.push(n);
                else if (nextExc.has(n)) toInclude.push(n);
                else excludedSet.has(n) ? toInclude.push(n) : toExclude.push(n);
              }
              delAll(nextInc, new Set(toExclude));
              addAll(nextExc, new Set(toExclude));
              delAll(nextExc, new Set(toInclude));
              addAll(nextInc, new Set(toInclude));
              return nextExc;
            });
            return nextInc;
          });
          return;
        }

        // default: replace with exactly targets (split by redux excluded)
        const nextIncluded = new Set();
        const nextExcluded = new Set();
        for (const n of targets) (excludedSet.has(n) ? nextExcluded : nextIncluded).add(n);
        setSelIncludedIds(() => nextIncluded);
        setSelExcludedIds(() => nextExcluded);
      },
      [categoryToSiteDirections, excludedSiteDirectionNames]
  );

  const modeOptions = [
    { name: "site", label: t("dashboards.overload_by.site") },
    { name: "site-direction", label: t("dashboards.overload_by.site_direction") },
    { name: "direction", label: t("dashboards.overload_by.direction") },
  ];

  return (
      <>
        <div className="field mb-4" style={{ maxWidth: 300 }}>
          <Select
              options={modeOptions}
              optionsValue="name"
              optionsLabel="label"
              label={t("dashboards_view.mode")}
              selectedItem={mode}
              onChange={setMode}
          />
        </div>

        <KPIChartSortHeader
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={(f, d) => {
              setSortBy(f);
              setSortDirection(d);
            }}
            fields={[
              { key: "name", labelKey: "dashboards_view.kpi1.sort.site" },
              { key: "total", labelKey: "dashboards_view.kpi1.sort.total" },
            ]}
        />

        <DedicatedBarChartWidget
            stacked
            id="kpi1a-bar-total-vehicles"
            title={t(titleKey)}
            xAxis={xAxis}
            series={displaySeries}
            colors={colors}
            isLoading={isLoading}
            dataLabelFunction={(val) => formatSLONumber(val, 0)}
            options={{
              xaxis: {
                labels: {
                  formatter: (val) => {
                    if (!val) return val;
                    if (mode === "site-direction") return val;
                    const [site, dir] = String(val).split(" | ");
                    const siteLabel =
                        locations?.[site?.trim()]?.locationName || site?.trim() || val;
                    return dir ? `${siteLabel} | ${dir.trim()}` : siteLabel;
                  },
                },
              },
              yaxis: {
                title: { text: t(yAxisKey) },
                labels: { formatter: (val) => formatSLONumber(val, 0) },
              },
              tooltip: {
                // followCursor: false,
                position: "bottom",
                // fixed: { enabled: true, position: "topRight" },
              },

            }}
            onBarClick={handleBarClick}
            perPointFill={perPointFill}
        />

        <ToggleSection
            sectionKey="footer"
            isHidden={isHidden}
            onToggle={togglerKey}
            title={t("dashboards.tabs.PKI3_content.filter")}
        >
          <ToggleTagGroupPanel
              label={t("dashboards_view.kpi3.site_direction_selection")}
              allItems={allSiteDirectionItems}
              excludedItems={excludedSiteDirectionItems}
              id="name"
              labelKeys={["name"]}
              controlledSelectedIncludedIds={selIncludedIds}
              controlledSelectedExcludedIds={selExcludedIds}
              onChangeSelectedIncludedIds={setSelIncludedIds}
              onChangeSelectedExcludedIds={setSelExcludedIds}
              onToggleItem={(item) => dispatch(toggleExcludedSiteDirection(item.name))}
              onIncludeAllSelectedFromExclude={(items) =>
                  dispatch(includeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onExcludeAllSelectedFromInclude={(items) =>
                  dispatch(excludeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onFlipSelection={() => {
                const all = allSiteDirectionItems.map((i) => i.name);
                const excluded = new Set(excludedSiteDirectionNames);
                const toInclude = all.filter((n) => excluded.has(n));
                const toExclude = all.filter((n) => !excluded.has(n));
                dispatch(setIncludedSiteDirections(toExclude));
                dispatch(setExcludedSiteDirections(toInclude));
              }}
          />
        </ToggleSection>
      </>
  );
};

export default VehicleTotalsChart;
